import { useMemo } from 'react'

import { useModals, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import handleCallbackAction from '@functions/handleCallbackAction'

import AddAccountingItemModal from '@modals/AddAccountingItemModal'
import CreateAgencyPayableInvoiceModal from '@modals/CreateAgencyPayableInvoiceModal'
import CreateOrEditCampaignContributionModal from '@modals/CreateOrEditCampaignContributionModal'
import CreateOrEditCreditNoteModal from '@modals/CreateOrEditCreditNoteModal'
import CreateOrEditInvoiceModal from '@modals/CreateOrEditInvoiceModal'
import EditReconciliationItemModal from '@modals/EditReconciliationItemModal'
import ManageActivityModal from '@modals/ManageActivityModal'

import type {
  ActivityModel,
  CampaignContributionModel,
  CampaignItemModel, CampaignModel, CreditNoteModel, InvoiceModel, OrderModel,
} from '@models/types'

import PageContent from './components/PageContent'

const defaultState = {
  showAddAccountingItemModal: false,
  showCreateAgencyPayableInvoiceModal: false,
  showCreateOrEditCampaignContributionModal: false,
  showCreateOrEditCreditNoteModal: false,
  showCreateOrEditInvoiceModal: false,
  showEditReconciliationItemModal: false,
  showManageActivityModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    AddAccountingItemModal: {
      closeModal: () => setState({ showAddAccountingItemModal: false }),
    },
    CreateAgencyPayableInvoiceModal: {
      // layered modals that both need to close upon invoice creation
      closeAllModals: () => setState({
        showAddAccountingItemModal: false,
        showCreateAgencyPayableInvoiceModal: false,
      }),
      closeModal: () => setState({ showCreateAgencyPayableInvoiceModal: false }),
      createInvoice: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditCreditNoteModal: {
      closeAllModals: () => setState({
        showAddAccountingItemModal: false,
        showCreateOrEditCreditNoteModal: false,
      }),
      closeModal: () => setState({ showCreateOrEditCreditNoteModal: false }),
      createCreditNote: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      createCreditNoteItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateCreditNote: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditCampaignContributionModal: {
      // layered modals that both need to close upon contribution creation
      closeAllModals: () => setState({
        showAddAccountingItemModal: false,
        showCreateOrEditCampaignContributionModal: false,
      }),
      closeModal: () => setState({ showCreateOrEditCampaignContributionModal: false }),
      createCampaignContribution: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      editCampaignContribution: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditInvoiceModal: {
      closeModal: () => setState({ showCreateOrEditInvoiceModal: false }),
      createInvoice: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateInvoice: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    EditReconciliationItemModal: {
      closeModal: () => setState({ showEditReconciliationItemModal: false }),
      updateReconciliationItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageActivityModal: {
      closeModal: () => setState({ showManageActivityModal: false }),
    },
  }

  return componentCallbacks[component] || {}
}

const OrderReconciliation = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showAddAccountingItemModal,
    showCreateAgencyPayableInvoiceModal,
    showCreateOrEditCampaignContributionModal,
    showCreateOrEditCreditNoteModal,
    showCreateOrEditInvoiceModal,
    showEditReconciliationItemModal,
    showManageActivityModal,
  } = state

  const modalContext = useModals({ setState })
  const { callbacks: { setModalData }, topModalKey } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      showAddAccountingItemModal: (payload: { campaign?: CampaignModel }) => {
        setModalData('AddAccountingItemModal', payload)
        setState({ showAddAccountingItemModal: true })
      },
      showCreateAgencyPayableInvoiceModal: (payload: { invoice?: InvoiceModel }) => {
        setModalData('CreateAgencyPayableInvoiceModal', payload)
        setState({ showCreateAgencyPayableInvoiceModal: true })
      },
      showCreateOrEditCampaignContributionModal: (payload: { campaignContribution?: CampaignContributionModel }) => {
        setModalData('CreateOrEditCampaignContributionModal', payload)
        setState({ showCreateOrEditCampaignContributionModal: true })
      },
      showCreateOrEditCreditNoteModal: (payload: { creditNote?: CreditNoteModel }) => {
        setModalData('CreateOrEditCreditNoteModal', payload)
        setState({ showCreateOrEditCreditNoteModal: true })
      },
      showCreateOrEditInvoiceModal: (payload: { invoice?: InvoiceModel }) => {
        setModalData('CreateOrEditInvoiceModal', payload)
        setState({ showCreateOrEditInvoiceModal: true })
      },
      showEditReconciliationItemModal: (payload: { item: (CampaignItemModel | OrderModel) }) => {
        setModalData('EditReconciliationItemModal', payload)
        setState({ showEditReconciliationItemModal: true })
      },
      showManageActivityModal: (payload: { activity: ActivityModel }) => {
        setModalData('ManageActivityModal', payload)
        setState({ showManageActivityModal: true })
      },
    },
  }), [topModalKey]) // modalContext dependency required if layering modals

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent />

        <AddAccountingItemModal
          callbacks={callbacks('AddAccountingItemModal', setState)}
          showModal={showAddAccountingItemModal}
        />

        <CreateAgencyPayableInvoiceModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateAgencyPayableInvoiceModal', setState)}
          showModal={showCreateAgencyPayableInvoiceModal}
        />

        <CreateOrEditCreditNoteModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditCreditNoteModal', setState)}
          showModal={showCreateOrEditCreditNoteModal}
        />

        <CreateOrEditCampaignContributionModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditCampaignContributionModal', setState)}
          showModal={showCreateOrEditCampaignContributionModal}
        />

        <CreateOrEditInvoiceModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditInvoiceModal', setState)}
          showModal={showCreateOrEditInvoiceModal}
        />

        <EditReconciliationItemModal
          // @ts-expect-error TS2322
          callbacks={callbacks('EditReconciliationItemModal', setState)}
          showModal={showEditReconciliationItemModal}
        />

        <ManageActivityModal
          callbacks={callbacks('ManageActivityModal', setState)}
          showModal={showManageActivityModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default OrderReconciliation
